/**
 * Centralized query key factory for TanStack Query.
 *
 * Every `queryKey` in the app must reference this object — never inline a key
 * array. That way invalidation helpers, devtools, and tests share a single
 * source of truth, and renaming a key is a one-line change.
 *
 * Namespaces:
 *   - `hyper.*`  — Hyperliquid user + market state
 *   - `wallet.*` — On-chain (Arbitrum USDC) balances
 *   - `fx.*`     — Currency conversion (KRW rate)
 *   - `trade.*`  — Trade-page specific shared state (orderbook, asset ctxs)
 */
export const queryKeys = {
	app: {
		settings: () => ["app", "settings"] as const,
	},
	auth: {
		me: (userId: string | null) => ["auth", "me", userId] as const,
	},
	referrals: {
		myStats: (userId: string | null) =>
			["referrals", "my-stats", userId] as const,
		myWithdrawals: (userId: string | null) =>
			["referrals", "my-withdrawals", userId] as const,
	},
	hyper: {
		account: (address: string | null) => ["hyper", "account", address] as const,
		accountXyz: (address: string | null) =>
			["hyper", "account-xyz", address] as const,
		userFees: (address: string | null) =>
			["hyper", "user-fees", address] as const,
		maxBuilderFee: (address: string | null, builder: string) =>
			["hyper", "max-builder-fee", address, builder] as const,
		userAbstraction: (address: string | null) =>
			["hyper", "user-abstraction", address] as const,
		openOrders: (address: string | null) =>
			["hyper", "open-orders", address] as const,
		spotBalances: (address: string | null) =>
			["hyper", "spot-balances", address] as const,
		ledger: (address: string | null) => ["hyper", "ledger", address] as const,
		fills: (address: string | null) => ["hyper", "fills", address] as const,
		fundingHistory: (address: string | null) =>
			["hyper", "funding-history", address] as const,
		orderHistory: (address: string | null) =>
			["hyper", "order-history", address] as const,
		twapHistory: (address: string | null) =>
			["hyper", "twap-history", address] as const,
		twapSliceFills: (address: string | null) =>
			["hyper", "twap-slice-fills", address] as const,
		portfolio: (address: string | null) =>
			["hyper", "portfolio", address] as const,
	},
	wallet: {
		balances: (address: string | null) =>
			["wallet", "balances", address] as const,
		transfers: (address: string | null) =>
			["wallet", "transfers", address] as const,
	},
	fx: {
		krwRate: () => ["fx", "krw-rate"] as const,
	},
	trade: {
		metaAndAssetContexts: () => ["trade", "meta-and-asset-contexts"] as const,
		orderbook: (
			apiCoin: string,
			nSigFigs: number,
			mantissa: number | undefined,
		) => ["trade", "orderbook", apiCoin, nSigFigs, mantissa] as const,
		bestBidAsk: (apiCoin: string) =>
			["trade", "best-bid-ask", apiCoin] as const,
		recentTrades: (apiCoin: string) =>
			["trade", "recent-trades", apiCoin] as const,
		activeAssetData: (address: string | null, apiCoin?: string) =>
			["trade", "active-asset-data", address, apiCoin] as const,
		baggerCandles: () => ["trade", "bagger-candles"] as const,
	},
};
